/**
 * TanStack column definitions for the entities data table.
 */

import { Building2 } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import type { ColumnDef } from "@tanstack/react-table";
import type { EntityRow } from "../types";
import { TYPE_COLOR, GROUP_CATEGORY_META } from "../constants";

export const entityColumns: ColumnDef<EntityRow>[] = [
  {
    accessorKey: "relatedTo",
    header: "Related To",
    size: 260,
    cell: ({ getValue }) => {
      const v = getValue<string | null>();
      if (!v) {
        return (
          <span className="text-xs text-muted-foreground/40">&mdash;</span>
        );
      }
      return (
        <Badge
          variant="secondary"
          className="max-w-[260px] truncate text-[10px] font-normal"
          title={v}
        >
          <Building2 className="mr-1 h-3 w-3 shrink-0" />
          {v}
        </Badge>
      );
    },
  },
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => {
      const entity = row.original;
      const detailParts = entity.roles
        .map((r) => r.detail)
        .filter(Boolean) as string[];
      return (
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium">{entity.name}</span>
              {entity.isPrimary && (
                <Badge className="shrink-0 border-primary/20 bg-primary/10 px-1 py-0 text-[9px] text-primary">
                  Primary
                </Badge>
              )}
              {entity.isCurrent === true &&
                entity.roles.some(
                  (r) =>
                    r.groupCategory === "officers" ||
                    r.groupCategory === "shareholders",
                ) && (
                  <Badge
                    color="green"
                    variant="soft"
                    className="shrink-0 px-1 py-0 text-[9px]"
                  >
                    Current
                  </Badge>
                )}
              {entity.isCurrent === false && (
                <Badge
                  color="amber"
                  variant="soft"
                  className="shrink-0 px-1 py-0 text-[9px]"
                >
                  Former
                </Badge>
              )}
            </div>
            {detailParts.map((detail, i) => (
              <p
                key={i}
                className="max-w-[300px] truncate text-xs text-muted-foreground"
              >
                {detail}
              </p>
            ))}
          </div>
        </div>
      );
    },
    filterFn: "includesString",
  },
  {
    accessorKey: "groupCategory",
    header: "Role",
    cell: ({ row }) => {
      const { roles } = row.original;
      const categories = roles.map((r) => r.groupCategory);
      const unique = [...new Set(categories)];
      return (
        <div className="flex flex-wrap gap-1">
          {unique.map((cat) => {
            const meta = GROUP_CATEGORY_META[cat] ?? GROUP_CATEGORY_META.other;
            const GroupIcon = meta.icon;
            return (
              <Badge
                key={cat}
                variant="outline"
                className={`text-[10px] ${meta.color} gap-1 border`}
              >
                <GroupIcon className="h-3 w-3" />
                {meta.label}
              </Badge>
            );
          })}
        </div>
      );
    },
    filterFn: (row, _columnId, filterValue) => {
      return row.original.roles.some((r) => r.groupCategory === filterValue);
    },
  },
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ getValue }) => {
      const type = getValue<string>();
      const color = TYPE_COLOR[type] ?? TYPE_COLOR.unknown;
      return (
        <Badge
          variant="outline"
          className={`text-[10px] capitalize ${color} border`}
        >
          {type}
        </Badge>
      );
    },
    filterFn: "equals",
  },
  {
    accessorKey: "acn",
    header: "ACN",
    cell: ({ getValue }) => {
      const v = getValue<string | null>();
      return v ? (
        <span className="font-mono text-xs tabular-nums">{v}</span>
      ) : (
        <span className="text-xs text-muted-foreground/40">&mdash;</span>
      );
    },
  },
  {
    accessorKey: "abn",
    header: "ABN",
    cell: ({ getValue }) => {
      const v = getValue<string | null>();
      return v ? (
        <span className="font-mono text-xs tabular-nums">{v}</span>
      ) : (
        <span className="text-xs text-muted-foreground/40">&mdash;</span>
      );
    },
  },
];
